import React from 'react'
import DOMPurify from 'dompurify'
import WCC from 'wcc'
import { Can, connectAbility } from '../../../lib/connectors/ability'
import { Cart } from '../../../lib/models/cart'
import { Stringable } from '../../../lib/types'
import { fullTitle as productFullTitle, formatAmount } from '../lib/product'
import { fetchProduct, IImage, DEFAULT_IMAGE, MoneyV2 } from '../lib/shopify_client'
import { withCheckout } from '../lib/with_checkout'
import { AddToCartButton } from './AddToCartButton'

// Shopify's descriptionHtml is authored-supplied HTML that lands in the
// storefront verbatim — sanitize before injecting to close CWE-79.
const sanitizeHtml = (html: string) => ({ __html: DOMPurify.sanitize(html) })

enum Status {
  Loading = 'LOADING',
  NotFound = 'NOT_FOUND',
  Ready = 'READY',
}

export interface IProps {
  productID: string
  variantID: string
  accountID: Stringable

  can: Can

  usingStorefrontCartApi: boolean

  // Set by the portal-themed mount (products/show.html.erb). When 'portal',
  // renders the redesigned card; otherwise the legacy layout is untouched.
  theme?: string

  fetchProduct: typeof fetchProduct
  addVariantToCart: (args: { variantID: string, quantity: number }) => any
}

interface IState {
  product?: {
    title: string
    descriptionHtml: string
    variants: Array<{
      id: string
      title: string
      isOutOfStock: boolean
      // Undefined when Shopify inventory tracking is off — treat as uncapped.
      quantityAvailable?: number
      price: MoneyV2
      image: { src: string, altText: string }
    }>
  }
  quantity: number
  status: Status
}

export const ProductDetail = withCheckout(
  class extends React.Component<IProps, IState> {
    public static defaultProps: Partial<IProps> = {
      fetchProduct: fetchProduct,
    }

    constructor(props: IProps) {
      super(props)

      this.handleAddToCart = this.handleAddToCart.bind(this)
      this.handleIncrement = this.handleIncrement.bind(this)
      this.handleDecrement = this.handleDecrement.bind(this)
      this.handleQuantityChange = this.handleQuantityChange.bind(this)

      this.state = {
        product: null,
        quantity: 1,
        status: Status.Loading,
      }
    }

    public componentDidMount() {
      const {
        productID,
        fetchProduct,
      } = this.props

      fetchProduct({ productID })
        .then((product) => {
          this.setState({
            product,
            status:  this.isProductFound({ product }) ? Status.Ready : Status.NotFound,
          })
        })
        .catch((result) => {
          this.setState({ status: Status.NotFound })
        })
    }

    public render() {
      const { status } = this.state

      switch (status) {
        case Status.Loading:
          return this.renderError({ message: 'Loading. Please Wait...' })
        case Status.NotFound:
          return this.renderError({ message: 'This product was not found!' })
        case Status.Ready:
        default:
          return this.props.theme === 'portal' ? this.renderPortalPage() : this.renderPage()
      }
    }

    private renderError({ message }: { message: string }): React.ReactElement<any> {
      return (
        <div className="row">
          <div className="column medium-12 text-center m5">
            {message}
          </div>
        </div>
      )
    }

    private renderPage(): React.ReactElement<any> {
      const { product } = this.state
      const variant = this.getVariant({ product })
      const {
        descriptionHtml: descriptionHTML,
      } = product
      const image: IImage = variant.image || DEFAULT_IMAGE

      const title = productFullTitle({ product, variant, showVariant: product.variants.length > 1 })
      const userID = WCC.currentUser ? WCC.currentUser.id : null

      const canAddToCart = this.props.can('create', new Cart({
        account_id: this.props.accountID,
        user_id: userID,
      }))

      return (
        <div>
          <div className="ministry__content__intro product__content__intro">
            <div className="row">
              <div className="column medium-6">
                <h3 className="text__title">{title}</h3>
                <span className="text__h4">Qty: 1</span>
                <div className="mt1">
                  {variant.isOutOfStock ?
                    <button className="product-button" disabled={true}>
                      Out Of Stock
                    </button> :
                    <AddToCartButton
                      className="btn btn-primary btn-xl"
                      onClick={this.handleAddToCart}
                      productName={title}
                      canAddToCart={canAddToCart}
                    >
                      + Add to cart
                    </AddToCartButton>}
                </div>
                <p className="mt05" dangerouslySetInnerHTML={sanitizeHtml(descriptionHTML)} />
              </div>
              <div className="column medium-6 large-5 xlarge-push-1">
                <img src={image.src} alt={image.altText} className="ministry__content__product-image" />
              </div>
            </div>
          </div>
        </div>
      )
    }

    private renderPortalPage(): React.ReactElement<any> {
      const { product, quantity } = this.state
      const variant = this.getVariant({ product })
      const { descriptionHtml: descriptionHTML } = product
      const image: IImage = variant.image || DEFAULT_IMAGE

      const title = productFullTitle({ product, variant, showVariant: product.variants.length > 1 })
      const userID = WCC.currentUser ? WCC.currentUser.id : null

      const canAddToCart = this.props.can('create', new Cart({
        account_id: this.props.accountID,
        user_id: userID,
      }))

      // Cap the stepper at available stock; undefined = untracked = uncapped.
      const stock = typeof variant.quantityAvailable === 'number' ? variant.quantityAvailable : undefined
      const atStockLimit = stock !== undefined && quantity >= stock

      return (
        <div className="portal-product-detail">
          <h1 className="portal-product-detail__title">{title}</h1>
          <div className="portal-product-detail__row">
            <img
              src={image.src}
              alt={image.altText}
              className="portal-product-detail__image"
            />
            <div className="portal-product-detail__details">
              <div className="portal-product-detail__qty">
                <span className="portal-product-detail__qty-label">Qty:</span>
                <span className="qty-stepper">
                  <button className="qty-stepper__btn" onClick={this.handleDecrement} aria-label="Decrease quantity">−</button>
                  <input
                    type="number"
                    pattern="[0-9]*"
                    min={1}
                    max={stock}
                    value={quantity}
                    onChange={this.handleQuantityChange}
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    className="qty-stepper__btn"
                    onClick={this.handleIncrement}
                    disabled={atStockLimit}
                    aria-label="Increase quantity"
                  >+</button>
                </span>
              </div>
              {atStockLimit &&
                <p className="portal-product-detail__stock-note">Only {stock} in stock.</p>}
              {variant.isOutOfStock ?
                <button className="product-button product-button--primary" disabled={true}>
                  Out Of Stock
                </button> :
                <AddToCartButton
                  className="product-button product-button--primary"
                  onClick={this.handleAddToCart}
                  productName={title}
                  canAddToCart={canAddToCart}
                  theme={this.props.theme}
                >
                  Add to Cart
                  <span className="product-button__price">{formatAmount(variant.price)}</span>
                </AddToCartButton>}
              {/* The product description carries the Figma "info block" content —
                  the shipping-cutoff notice and the "Version X.X" line. */}
              {descriptionHTML &&
                <div
                  className="portal-product-detail__info"
                  dangerouslySetInnerHTML={sanitizeHtml(descriptionHTML)}
                />}
            </div>
          </div>
        </div>
      )
    }

    private getVariant({ product }: { product: IState['product'] }) {
      const { variantID } = this.props

      if (variantID) {
        return product.variants.find((variant) => variant.id == variantID)
      } else {
        return product.variants[0]
      }
    }

    private isProductFound({ product }: { product: IState['product'] }): boolean {
      const variant = this.getVariant({ product })
      return !!(product && variant)
    }

    private handleAddToCart() {
      const {
        addVariantToCart,
        variantID,
      } = this.props

      return addVariantToCart({ variantID, quantity: this.state.quantity })
    }

    // Available stock for the current variant, or Infinity when inventory
    // tracking is off (quantityAvailable undefined) so we don't wrongly cap.
    private stockLimit(): number {
      const variant = this.state.product && this.getVariant({ product: this.state.product })
      const available = variant && variant.quantityAvailable
      return typeof available === 'number' ? available : Infinity
    }

    private handleIncrement() {
      const max = this.stockLimit()
      this.setState((state) => ({ quantity: Math.min(max, state.quantity + 1) }))
    }

    private handleDecrement() {
      this.setState((state) => ({ quantity: Math.max(1, state.quantity - 1) }))
    }

    private handleQuantityChange(event: React.ChangeEvent<HTMLInputElement>) {
      // valueAsNumber is NaN for empty/non-numeric input — clamp to 1 (there is
      // no "remove" semantics on a product page, unlike the cart line). Cap at
      // available stock so the user can't add more than exists.
      const value = Math.floor(event.target.valueAsNumber)
      const atLeastOne = Number.isFinite(value) && value >= 1 ? value : 1
      this.setState({ quantity: Math.min(this.stockLimit(), atLeastOne) })
    }
  },
)

export default connectAbility(ProductDetail)
